import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import StatCard from '../components/StatCard';
import CreateProjectModal from '../components/CreateProjectModal';
import { api } from '../utils/api';
import { useI18n } from '../i18n/I18nProvider';

const DashboardPage = () => {
  const [projects, setProjects] = useState([]);
  const [account, setAccount]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const { t, formatDate } = useI18n();

  useEffect(() => {
    Promise.all([
      api.listProjects().then(r => r.ok ? r.json() : []),
      api.getAccount().then(r => r.ok ? r.json() : null),
    ]).then(([projs, acc]) => {
      setProjects(projs || []);
      setAccount(acc);
      setLoading(false);
    });
  }, []);

  const activeProjects = projects.filter(p => p.Enabled).length;
  const totalChecks    = projects.reduce((s, p) => s + (p.Checks?.length || 0), 0);
  // Block project creation until the e-mail is confirmed (PIN-254): the backend
  // rejects it anyway, so disable the entry points and explain why on hover.
  const unverified     = account && !account.EmailVerified;

  const header = (
    <>
      <h1 className="uh-title">{t('dashboard.title')}</h1>
      <div className="uh-actions">
        {account && <span style={{ fontSize: 14, color: 'var(--subtext)' }}>{account.Name}</span>}
        <button className="uh-btn uh-btn-primary" onClick={() => setShowCreate(true)} disabled={unverified} title={unverified ? t('verifyBanner.text') : undefined}>{t('dashboard.newProject')}</button>
      </div>
    </>
  );

  return (
    <Layout sidebar={<Sidebar />} header={header}>
      <div className="uh-grid">
        <StatCard title={t('dashboard.projects')}       value={loading ? '…' : projects.length} accent="accent-1" />
        <StatCard title={t('dashboard.active')}         value={loading ? '…' : activeProjects}  accent="accent-2" />
        <StatCard title={t('dashboard.checksConfigured')} value={loading ? '…' : totalChecks}  accent="accent-3" />
      </div>

      <div className="uh-section">
        <div className="uh-section-head">
          <h2 style={{ margin: 0, fontSize: 16 }}>{t('dashboard.yourProjects')}</h2>
          <Link to="/projects" className="uh-link" style={{ fontSize: 13 }}>{t('dashboard.viewAll')}</Link>
        </div>
        {loading ? (
          <p className="uh-empty">{t('common.loading')}</p>
        ) : projects.length === 0 ? (
          <p className="uh-empty">{t('dashboard.noProjectsYet')} <button type="button" className="uh-link" onClick={() => setShowCreate(true)} disabled={unverified} title={unverified ? t('verifyBanner.text') : undefined} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: unverified ? 'not-allowed' : 'pointer' }}>{t('dashboard.createOne')}</button></p>
        ) : (
          <table className="uh-table">
            <thead>
              <tr>
                <th>{t('dashboard.name')}</th>
                <th>{t('dashboard.status')}</th>
                <th>{t('dashboard.checks')}</th>
                <th>{t('dashboard.created')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {projects.slice(0, 8).map(p => (
                <tr key={p.ID}>
                  <td data-label={t('dashboard.name')}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <span
                        className={`uh-status-dot uh-status-dot--${p.Enabled ? 'ok' : 'muted'}`}
                        aria-hidden="true"
                      />
                      <span>{p.ProjectName}</span>
                    </span>
                  </td>
                  <td data-label={t('dashboard.status')}>
                    <span className={`uh-badge ${p.Enabled ? 'ok' : 'muted'}`}>
                      {p.Enabled ? t('common.active') : t('common.paused')}
                    </span>
                  </td>
                  <td data-label={t('dashboard.checks')}>{p.Checks?.length || 0}</td>
                  <td data-label={t('dashboard.created')} style={{ fontSize: 12, color: 'var(--subtext)' }}>
                    {formatDate(p.CreatedAt, { dateStyle: 'medium' })}
                  </td>
                  <td className="uh-td-actions">
                    <Link to={`/projects/${p.ID}`} className="uh-btn uh-btn-sm">{t('common.open')}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreate={p => setProjects(ps => [p, ...ps])}
        />
      )}
    </Layout>
  );
};

export default DashboardPage;
